import {
  Component,
  DestroyRef,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { catchError, filter, map, of, startWith, switchMap } from 'rxjs';
import { LoginResponse } from 'angular-auth-oidc-client';
import { AuthFacade } from './auth/auth.facade';
import { IdpConfigId } from './auth/models/idp-config-id.enum';
import { LoggedUserDataService } from './services/data/logged-user-data.service';
import { ProfileDataService } from './services/data/profile-data.service';
import { LoggingService } from './services/logging.service';
import { DatadogRumFacade } from './services/observability';
import { ACTIVE_IDP_KEY, PENDING_IDP_KEY } from './auth/models/auth-storage-keys';

const ERROR_ROUTE_PREFIXES = [
  '/login-error',
  '/auth-error',
  '/etm-account-inactive',
  '/product-entitlement-inactive',
];

function isErrorRoute(url: string): boolean {
  return ERROR_ROUTE_PREFIXES.some((prefix) => url.startsWith(prefix));
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
})
export class AppComponent implements OnInit {
  private readonly authFacade = inject(AuthFacade);
  private readonly loggedUserDataService = inject(LoggedUserDataService);
  private readonly profileDataService = inject(ProfileDataService);
  private readonly router = inject(Router);
  private readonly logger = inject(LoggingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly datadogRum = inject(DatadogRumFacade);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  private readonly isAuthOverlayBypassed = computed(() => {
    const url = this.currentUrl();
    return url.endsWith('/main') || url === '/' || isErrorRoute(url);
  });

  /**
   * True while the Ping → Okta fallback chain is still in progress.
   * Initialized from sessionStorage so that it is already true on the page
   * reload that occurs after each IdP redirect.
   *
   * NOTE: when Okta support is removed in the future, a single-IdP failure
   * must call `this.isFallbackInProgress.set(false)` before navigating to
   * /login-error so the overlay does not permanently block the error page.
   */
  private readonly isFallbackInProgress = signal(!!sessionStorage.getItem(PENDING_IDP_KEY));

  readonly userAuthenticated: Signal<boolean> = computed(
    () => this.authFacade.isAuthenticated() || this.isAuthOverlayBypassed(),
  );

  readonly isFetchingProfile: Signal<boolean> = toSignal(
    this.profileDataService.isFetchingUserProfile$(),
    { initialValue: false },
  );

  readonly showInitialLoading = computed(() => {
    const url = this.currentUrl();
    if (url.startsWith('/sso')) return true;
    if (this.isFallbackInProgress()) return true;
    if (isErrorRoute(url)) return false;
    return !this.authFacade.isAuthenticated() || this.isFetchingProfile();
  });

  constructor() {
    effect(() => {
      const claims = this.authFacade.userClaims();
      if (claims) {
        this.loggedUserDataService.setUser(claims);
      }
    });

    // Mirror the authenticated user into Datadog RUM. No-op while RUM is disabled.
    effect(() => {
      const claims = this.authFacade.userClaims();
      if (claims) {
        this.datadogRum.setUser({
          id: typeof claims.sub === 'string' && claims.sub.length > 0 ? claims.sub : 'unknown',
          name: typeof claims.name === 'string' ? claims.name : undefined,
          email: typeof claims.email === 'string' ? claims.email : undefined,
        });
      } else {
        this.datadogRum.clearUser();
      }
    });
  }

  ngOnInit(): void {
    this.authFacade
      .checkAuth()
      .pipe(
        catchError((error) => {
          this.logger.error('Authentication state error', error);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((responses) => {
        if (!Array.isArray(responses)) return;
        this.handleAuthFallback(responses);
      });

    this.authFacade.silentRenewFailed$
      .pipe(
        filter(() => !this.isFallbackInProgress()),
        switchMap(() => {
          const activeId = this.authFacade.activeConfigId();
          if (!activeId) {
            return of({ isAuthenticated: false } as LoginResponse);
          }
          this.logger.warn(
            `[AppComponent] Silent renew failed for ${activeId}. Attempting forceRefreshSession() before redirecting.`,
          );
          return this.authFacade.forceRefreshSession(activeId).pipe(
            catchError((err) => {
              this.logger.error('[AppComponent] forceRefreshSession failed', err);
              return of({ isAuthenticated: false } as LoginResponse);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => {
        if (response?.isAuthenticated) {
          this.logger.info('[AppComponent] Token recovered via forceRefreshSession.');
          return;
        }
        this.logger.warn('[AppComponent] Silent token renewal failed. Redirecting to /auth-error.');
        this.router.navigateByUrl('/auth-error');
      });
  }

  /**
   * Handles the Ping → Okta → /login-error fallback chain.
   * `pendingIdp` in sessionStorage tracks which IdP was last attempted across the redirect cycle.
   */
  private handleAuthFallback(responses: LoginResponse[]): void {
    const pendingIdp = sessionStorage.getItem(PENDING_IDP_KEY);
    if (!pendingIdp) return;

    const authenticated = responses.find((r) => r.isAuthenticated);
    if (authenticated?.configId) {
      sessionStorage.setItem(ACTIVE_IDP_KEY, authenticated.configId);
      sessionStorage.removeItem(PENDING_IDP_KEY);
      this.isFallbackInProgress.set(false);

      const inactiveId =
        authenticated.configId === IdpConfigId.Okta ? IdpConfigId.Oidc : IdpConfigId.Okta;
      try {
        this.authFacade.logoutLocalFor(inactiveId);
      } catch (err) {
        this.logger.warn(`[AppComponent] Unable to clear storage for ${inactiveId}`, err);
      }

      this.router.navigateByUrl('/main');
      return;
    }

    if (pendingIdp === IdpConfigId.Oidc) {
      sessionStorage.setItem(PENDING_IDP_KEY, IdpConfigId.Okta);
      // isFallbackInProgress stays true — page will immediately redirect to Okta
      this.authFacade.loginWith(IdpConfigId.Okta, { prompt: 'none' });
      return;
    }

    this.logger.warn('[AppComponent] Both IdPs failed. Redirecting to /login-error.');
    sessionStorage.removeItem(PENDING_IDP_KEY);
    this.isFallbackInProgress.set(false);
    this.router.navigateByUrl('/login-error');
  }
}
